// ─────────────────────────────────────────────
//  ENUMS
// ─────────────────────────────────────────────
export enum JobStatus {
  Draft = 0,
  PendingReview = 1,
  Active = 2,
  Quoted = 3,
  Accepted = 4,
  ContractGenerated = 5,
  InProgress = 6,
  Completed = 7,
  PodVerified = 8,
  PaymentReleased = 9,
  ReviewUnlocked = 10,
  Cancelled = 11,
  Rejected = 12,
  CarrierAccepted = 13,
  Paused = 14,
}

export enum PaymentTerm {
  Cash = 0,
  Credit = 1,
}

export enum QuoteStatus {
  Pending = 0,
  Accepted = 1,
  Rejected = 2,
  Withdrawn = 3,
  Expired = 4,
}

export enum AvailableLoadStatus {
  Active    = 0,
  Converted = 1,
  Expired   = 2,
  Cancelled = 3,
}

export interface AvailableLoadDto {
  id: string;
  carrierId: string;
  carrierName?: string | null;
  vehicleType: VehicleType;
  pickupAddress: string;
  pickupLat: number;
  pickupLng: number;
  deliveryAddress: string;
  deliveryLat: number;
  deliveryLng: number;
  distanceMiles: number;
  availableFrom: string;
  availableUntil?: string | null;
  availablePallets: number;
  totalCapacityPallets?: number | null;
  availableWeightKg?: number | null;
  availableLoadingMetresLdm?: number | null;
  notes?: string | null;
  status: AvailableLoadStatus;
  convertedJobId?: string | null;
  convertedAt?: string | null;
  createdAt: string;
  distanceFromSearchMiles?: number | null;
}

export enum JobType {
  SameDayDelivery = 0,
  ScheduledDelivery = 1,
  RelayDelivery = 2,
  VehicleRepair = 3,
  Removal = 4,
  FreightHaulage = 5,
  MultiDropDelivery = 6,
  MultiPickupDropDelivery = 7,
}

export enum VehicleType {
  Motorcycle = 0,
  Car        = 1,
  SmallVan   = 2,
  MidiVan    = 3,
  LWBVan     = 4,
  LutonVan   = 5,
  Tonne7_5   = 6,
  Tonne12    = 7,
  Tonne18    = 8,
  Tonne26    = 9,
  Artic      = 10,
}

export enum RelaySegmentStatus {
  Pending = 0,
  Assigned = 1,
  PickedUp = 2,
  InTransit = 3,
  HandedOver = 4,
  Completed = 5,
  Failed = 6,
}

export enum PaymentStatus {
  Pending = 1,
  Processing = 2,
  Released = 3,
  Failed = 4,
  Refunded = 5,
  OnHold = 6,
}

export enum DropStatus {
  Pending = 0,
  EnRoute = 1,
  Arrived = 2,
  Completed = 3,
  Failed = 4,
  Skipped = 5,
}

export enum PickupStatus {
  Pending = 0,
  EnRoute = 1,
  Arrived = 2,
  Collected = 3,
  Failed = 4,
  Skipped = 5,
}

// ─────────────────────────────────────────────
//  JOB TYPES
// ─────────────────────────────────────────────
export interface CreateJobRequest {
  jobType: JobType;
  title: string;
  description: string;
  isSameDay: boolean;
  scheduledAt: string;
  pickupAddress: string;
  pickupLat: number;
  pickupLng: number;
  deliveryAddress: string;
  deliveryLat: number;
  deliveryLng: number;
  goodsDescription: string;
  estimatedValueGbp?: number;
  requiredVehicleType: VehicleType;
  isRelayJob: boolean;
  isClosedBidding?: boolean;
  /** Carrier/Workshop only: post straight to my own fleet, never listed or quotable. */
  isInternal?: boolean;
  paymentTerm?: PaymentTerm;
  weightKg?: number;
  loadingMetresLdm?: number;
  isStackable?: boolean;
  requiresTailLift?: boolean;
  requiresTwoMan?: boolean;
  requiresHiab?: boolean;
  /** Repair jobs only: standard service codes ('mot', 'servicing', ...). Drives workshop auto-quote matching. */
  repairServiceTypes?: string[];
  /** @deprecated Kept so existing callers compile; the API reads repairServiceTypes. */
  repairServiceType?: string;
}

export interface JobSummaryDto {
  id: string;
  referenceNumber: string;
  jobType: JobType;
  status: JobStatus;
  title: string;
  isSameDay: boolean;
  scheduledAt: string;
  pickupAddress: string;
  deliveryAddress: string;
  distanceMiles: number;
  quoteCount: number;
  customerName: string;
  createdAt: string;
  customerEditedAt?: string | null;
  requiredVehicleType?: VehicleType;
  requiresTwoMan?: boolean;
  requiresTailLift?: boolean;
  requiresHiab?: boolean;
  /** Haversine distance from the searching carrier's position to the pickup. Only populated on the carrier job feed. */
  distanceFromCarrierMiles?: number;
  isFavourited?: boolean;
  /** Coordinates — populated on the carrier job feed for map plotting. */
  pickupLat?: number;
  pickupLng?: number;
  deliveryLat?: number;
  deliveryLng?: number;
  dropCount?: number;
  pickupCount?: number;
  isPickupAddressHidden?: boolean;
  isDeliveryAddressHidden?: boolean;
  /** True for a carrier's own fleet job — off-market, no quotes, self-assigned. */
  isInternal?: boolean;
  /** Charging zones the route passes through. Route info, not a quote breakdown. */
  routeZoneCodes?: string[] | null;
  /** True when zone names were withheld because an address on this job is hidden. */
  routeZonesCoarsened?: boolean;
}

export interface FavouriteJobSummary {
  favouriteId: string;
  jobId: string;
  referenceNumber: string;
  jobType: JobType;
  status: JobStatus;
  title: string;
  pickupAddress: string;
  deliveryAddress: string;
  distanceMiles: number;
  scheduledAt: string;
  requiredVehicleType: VehicleType;
  quoteCount: number;
  isClosedBidding: boolean;
  isFavourited: boolean;
  createdAt: string;
}

export interface AvailableJobsResponse {
  totalCount: number;
  jobs: FavouriteJobSummary[];
}

export interface JobDropDto {
  id: string;
  jobId: string;
  dropOrder: number;
  address: string;
  postCode: string;
  latitude: number;
  longitude: number;
  status: DropStatus;
  city?: string | null;
  goodsDescription?: string | null;
  recipientName?: string | null;
  arrivedAt?: string | null;
  completedAt?: string | null;
  failureReason?: string | null;
  hasPod: boolean;
  podPhotoUrls?: string[] | null;
}

export interface JobPickupDto {
  id: string;
  jobId: string;
  pickupOrder: number;
  address: string;
  postCode: string;
  latitude: number;
  longitude: number;
  status: PickupStatus;
  city?: string | null;
  goodsDescription?: string | null;
  senderName?: string | null;
  arrivedAt?: string | null;
  collectedAt?: string | null;
  failureReason?: string | null;
  hasPoc: boolean;
  pocPhotoUrls?: string[] | null;
  pocSignatureUrl?: string | null;
  pocSenderName?: string | null;
  pocLatitude?: number | null;
  pocLongitude?: number | null;
  pocCapturedAt?: string | null;
}

export interface JobDetailDto extends JobSummaryDto {
  customerId: string;
  description: string;
  pickupLat: number;
  pickupLng: number;
  deliveryLat: number;
  deliveryLng: number;
  goodsDescription: string;
  estimatedValueGbp?: number | null;
  requiredVehicleType: VehicleType;
  isRelayJob: boolean;
  isClosedBidding?: boolean;
  quotes: QuoteSummaryDto[];
  relaySegments: RelaySegmentDto[];
  contract?: ContractDto;
  goodsImageUrls?: string[] | null;
  paymentTerm?: PaymentTerm;
  carrierAcceptedAt?: string | null;
  /** Set once a quote is accepted, or at creation for an internal fleet job. */
  carrierId?: string | null;
  assignedVehicleId?: string | null;
  assignedEmployeeId?: string | null;
  podCapturedAt?: string | null;
  podPhotoUrls?: string[] | null;
  podSignatureUrl?: string | null;
  podRecipientName?: string | null;
  pocCapturedAt?: string | null;
  pocPhotoUrls?: string[] | null;
  pocSignatureUrl?: string | null;
  pocSenderName?: string | null;
  weightKg?: number | null;
  loadingMetresLdm?: number | null;
  isStackable?: boolean;
  drops?: JobDropDto[] | null;
  pickups?: JobPickupDto[] | null;
  /** True for a carrier's own fleet job — off-market, no quotes, self-assigned. */
  isInternal?: boolean;
  /** Repair jobs only: the standard service codes the customer picked. */
  repairServiceTypes?: string[] | null;
  routeZoneCodes?: string[] | null;
  routeZonesCoarsened?: boolean;
}

export interface PaymentIntentDto {
  intentId: string;
  clientToken: string;
  amount: number;
}

export interface EditJobRequest {
  title: string;
  description: string;
  isSameDay: boolean;
  scheduledAt: string;
  goodsDescription: string;
  estimatedValueGbp?: number;
  requiredVehicleType: VehicleType;
}

// ─────────────────────────────────────────────
//  QUOTE TYPES
// ─────────────────────────────────────────────
export interface SubmitQuoteRequest {
  jobId: string;
  amount: number;         // Delivery jobs. Ignored for workshop jobs (computed server-side).
  message?: string;
  labourAmount?: number;  // Workshop/repair jobs only: labour cost inc. VAT
  partsAmount?: number;   // Workshop/repair jobs only: parts cost inc. VAT
}

export interface QuoteSummaryDto {
  id: string;
  carrierId: string;
  carrierName: string;
  averageCarrierRating: number;
  carrierCompletedJobs: number;
  carrierVehicleType: VehicleType;
  amount: number;
  /** Workshop quotes only (inc. VAT). Null for delivery quotes. */
  labourAmount?: number | null;
  /** Workshop quotes only (inc. VAT). Null for delivery quotes. */
  partsAmount?: number | null;
  /** Null when a Customer is viewing a workshop job (commission is charged to the workshop). */
  platformCommission?: number | null;
  /** Null when a Customer is viewing a workshop job. */
  carrierNetAmount?: number | null;
  status: QuoteStatus;
  message?: string;
  expiresAt: string;
  isBelowFloor: boolean;
  submittedAt: string;
  acceptedAt?: string | null;
  /** Workshop location latitude — present when the workshop has geocoded their address. */
  bidderLat?: number | null;
  /** Workshop location longitude — present when the workshop has geocoded their address. */
  bidderLng?: number | null;
}

export interface QuoteCalculationDto {
  quoteAmount: number;
  /** Workshop only (inc. VAT); null for delivery. */
  labourAmount?: number | null;
  /** Workshop only (inc. VAT); null for delivery. */
  partsAmount?: number | null;
  /** Percentage taken is set by PlatformFeeConfig: labour-only for workshops, total for delivery. */
  platformCommission: number;
  workshopOrCarrierNet: number;
  cashbackForCustomer?: number | null;
  referralAmount?: number | null;
  netToTransitDeal: number;
}

// ─────────────────────────────────────────────
//  RELAY TYPES
// ─────────────────────────────────────────────
export interface RelaySegmentDto {
  id: string;
  segmentOrder: number;
  assignedCarrierName?: string;
  pickupAddress: string;
  pickupLat?: number;
  pickupLng?: number;
  deliveryAddress: string;
  deliveryLat?: number;
  deliveryLng?: number;
  distanceMiles: number;
  relayFeePerMile: number;
  totalRelayFee: number;
  status: RelaySegmentStatus;
  handoverDeadline?: string;
  handoverInstructions?: string;
}

// ─────────────────────────────────────────────
//  CONTRACT
// ─────────────────────────────────────────────
export interface ContractDto {
  id: string;
  contractReference: string;
  contractPdfUrl: string;
  agreedPrice: number;
  commissionAmount: number;
  generatedAt: string;
  cancellationDeadline: string;
  lastRegeneratedAt?: string | null;
}

// Row shape for "My Contracts" — see ContractSummaryDto in TransitDeal.DataAccess.DTOs.Dtos.
export interface ContractSummaryDto {
  id: string;
  jobId: string;
  jobReference: string;
  jobType: JobType;
  jobStatus: JobStatus;
  otherPartyName: string;
  agreedPrice: number;
  platformFeeAmount: number;
  generatedAt: string;
  lastRegeneratedAt: string | null;
  cancellationDeadline: string | null;
}

// ─────────────────────────────────────────────
//  PAYMENT
// ─────────────────────────────────────────────
export interface CarrierEarningsDashboardDto {
  totalEarnings: number;
  referralEarnings: number;
  cashbackWallet: number;
  completedJobs: number;
  recentPayments: PaymentSummaryDto[];
  /** Total COD outstanding across active jobs (gross, before platform fee). */
  totalCodOutstanding: number;
  /** Platform fee owed on outstanding COD — carrier must remit this after collecting. */
  platformFeeOnCodOwed: number;
  /** Total gross value of all pending/processing payments. */
  pendingGrossAmount: number;
  /** Platform fee (rate set by PlatformFeeConfig) on all pending/processing payments not yet settled. */
  pendingPlatformFee: number;
}

export interface PaymentSummaryDto {
  id: string;
  jobId: string;
  jobReference: string;
  grossAmount: number;
  commissionDeducted: number;
  netAmount: number;
  onlineAmount?: number;
  codAmount?: number;
  status: PaymentStatus;
  releasedAt?: string;
}

export interface MonthlyStatementLineDto {
  paymentId: string;
  jobId: string;
  jobReference: string;
  date: string;
  grossAmount: number;
  platformFee: number;
  netAmount: number;
  onlineAmount: number;
  codAmount: number;
  status: string;
  codRemittancePending: boolean;
}

export interface MonthlyStatementDto {
  year: number;
  month: number;
  monthName: string;
  lines: MonthlyStatementLineDto[];
  totalGross: number;
  totalPlatformFee: number;
  totalNet: number;
  totalOnline: number;
  totalCod: number;
}

// ─────────────────────────────────────────────
//  CHATBOT
// ─────────────────────────────────────────────
export interface ChatbotMessageRequest {
  message: string;
  sessionId?: string;
  userId?: string;
}

export interface ChatbotMessageResponse {
  reply: string;
  sessionId: string;
  profileCompleteness: number;
  suggestedActions: string[];
  extractedJobData?: CreateJobRequest;
}

// ─────────────────────────────────────────────
//  MAPS
// ─────────────────────────────────────────────
export interface RouteResponse {
  distanceMiles: number;
  estimatedMinutes: number;
  encodedPolyline: string;
  relaySegments: RelaySegmentRouteDto[];
}

/** Live-traffic-aware ETA (TomTom, via /api/maps/live-route) - see ILiveTrafficRoutingService. */
export interface LiveRouteResponse {
  distanceMiles: number;
  estimatedMinutes: number;
  etaUtc: string;
}

export interface RelaySegmentRouteDto {
  order: number;
  fromLat: number;
  fromLng: number;
  toLat: number;
  toLng: number;
  distanceMiles: number;
  relayFee: number;
}

// ─────────────────────────────────────────────
//  PROFILE TYPES
// ─────────────────────────────────────────────
export interface UserProfileDto {
  userId: string;
  firstName: string;
  lastName: string;
  displayName?: string;
  email: string;
  phoneNumber: string;
  role: string;
  registrationStatus: string;
  // Address
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  postcode?: string;
  // Business
  isBusinessAccount: boolean;
  businessName?: string;
  vatNumber?: string;
  companiesHouseNumber?: string;
  directorName?: string;
  // Carrier-specific
  vehicleType?: string;
  vehicleRegistration?: string;
  dvlaLicenceNumber?: string;
  licenceCategory?: string;
  hireRewardInsurer?: string;
  hireRewardPolicyRef?: string;
  hireRewardExpiry?: string;
  preferredAreas?: string;
  voiceChatEnabled?: boolean;
  workshopServiceTypes?: string[];
  // Bank & payment details (all roles)
  bankAccountName?: string;
  bankSortCode?: string;
  bankAccountNumber?: string;
  stripeConnectedAccountId?: string;
  // Identity verification
  hasIdentityVerification?: boolean;
  identityDocumentType?: string;
  identityVerifiedAt?: string;
  // Credit payment — Admin/Ops manual approval gate
  creditApprovalStatus?: 'None' | 'Pending' | 'Approved' | 'Rejected';
  creditApprovedAt?: string;
  creditRejectionReason?: string;
  canRequestCredit?: boolean;
  isCreditPaymentEligible?: boolean;
  avatarUrl?: string;
}

export interface UpdateProfileRequest {
  firstName: string;
  lastName: string;
  displayName?: string;
  phoneNumber?: string;
  // Address
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  postcode?: string;
  // Business
  isBusinessAccount: boolean;
  businessName?: string;
  vatNumber?: string;
  companiesHouseNumber?: string;
  directorName?: string;
  // Carrier-specific
  vehicleType?: string;
  vehicleRegistration?: string;
  dvlaLicenceNumber?: string;
  licenceCategory?: string;
  hireRewardInsurer?: string;
  hireRewardPolicyRef?: string;
  hireRewardExpiry?: string;
  preferredAreas?: string;
  voiceChatEnabled?: boolean;
  workshopServiceTypes?: string[];
  // Bank & payment details (all roles)
  bankAccountName?: string;
  bankSortCode?: string;
  bankAccountNumber?: string;
  stripeConnectedAccountId?: string;
}

// ─────────────────────────────────────────────
//  JOB ALERT TYPES
// ─────────────────────────────────────────────
export interface JobAlertPreference {
  id: string;
  postcode: string;
  radiusMiles: number;
  vehicleType: VehicleType | null;
  isEnabled: boolean;
  createdAt: string;
  lastMatchedAt: string | null;
}

export interface CreateJobAlertRequest {
  postcode: string;
  centroidLat: number;
  centroidLng: number;
  radiusMiles: number;
  vehicleType?: VehicleType;
}

// ─────────────────────────────────────────────
//  TRAFFIC NEWS & WEATHER TYPES
//  Shapes returned by the backend's TrafficNewsController/WeatherController — both proxy a
//  third-party feed server-side (see TransitDeal.Api.Services.TrafficNewsService/WeatherService),
//  so these mirror TrafficIncidentDto/HourlyWeatherDto exactly. Same shape as the mobile app's
//  equivalent types in mobile/src/types/index.ts.
// ─────────────────────────────────────────────
export interface TrafficIncident {
  id: string;
  title: string;
  description: string;
  category: string;
  severity: string;
  roadName?: string;
  lat: number;
  lng: number;
  distanceMiles: number;
  startDate?: string;
  endDate?: string;
}

export interface HourlyWeather {
  time: string;
  temperatureC: number;
  feelsLikeC: number;
  precipitationProbability: number;
  windSpeedMph: number;
  weatherCode: number;
  summary: string;
  icon: string;
}

// ─────────────────────────────────────────────
//  DOCUMENT UPLOAD TYPES
// ─────────────────────────────────────────────
export enum DocumentType {
  HireRewardInsurance       = 'HireRewardInsurance',
  GoodsInTransitInsurance   = 'GoodsInTransitInsurance',
  PublicLiabilityInsurance  = 'PublicLiabilityInsurance',
  MotCertificate            = 'MotCertificate',
  DrivingLicence            = 'DrivingLicence',
  IdentityDocument          = 'IdentityDocument',
}

export type DocumentStatus = 'Pending' | 'Approved' | 'Rejected';

export interface DocumentUpload {
  documentId: string;
  documentType: DocumentType;
  userRole: string;
  originalFileName: string;
  reviewStatus: DocumentStatus;
  expiryDate: string | null;
  reviewNotes: string | null;
  uploadedAt: string;
  reviewedAt: string | null;
}

// ─────────────────────────────────────────────
//  SIGNALR EVENTS
// ─────────────────────────────────────────────
export interface JobApprovedEvent {
  jobId: string;
  referenceNumber: string;
}

export interface QuoteReceivedEvent {
  quoteId: string;
  jobId: string;
  amount: number;
  carrierNetAmount: number;
}

export interface ContractGeneratedEvent {
  contractId: string;
  contractReference: string;
  contractPdfUrl: string;
}

export interface PaymentReleasedEvent {
  paymentId: string;
  jobId: string;
  netAmount: number;
  releasedAt: string;
}

export interface CarrierLocationUpdate {
  carrierId: string;
  lat: number;
  lng: number;
  timestamp: string;
}

export interface ChatMessage {
  id: string;
  jobId: string;
  senderId: string;
  recipientId?: string;
  content: string;
  sentAt: string;
  isRead: boolean;
  isMine?: boolean;
}

export interface BazaarChatMessage {
  id: string;
  listingId: string;
  senderId: string;
  recipientId: string;
  content: string;
  sentAt: string;
  isRead: boolean;
  isMine?: boolean;
}

// ─────────────────────────────────────────────
//  USER VEHICLE TYPES
//  Vehicles belong to any role now - Customer, Carrier or Workshop.
//  Identity fields are DVSA-sourced and read-only in the UI.
// ─────────────────────────────────────────────
export type InsuranceStatus = 0 | 1 | 2; // 0=Unknown 1=Pending 2=Verified

/** Mirrors TransitDeal.Domain.Enums.MotStatus. */
export enum MotStatus {
  Unknown      = 0,
  Valid        = 1,
  Expired      = 2,
  /** Never tested and the first MOT is not due yet - compliant, not a problem. */
  NotYetDue    = 3,
  /** DVSA holds no record; a manual certificate is the only route to verification. */
  NoDvsaRecord = 4,
}

/** Mirrors TransitDeal.Domain.Enums.MotSource. */
export enum MotSource {
  None   = 0,
  Dvsa   = 1,
  Manual = 2,
}

export interface MotDefect {
  text?: string;
  /** DANGEROUS | MAJOR | MINOR | FAIL | PRS | ADVISORY | USER ENTERED */
  type?: string;
  dangerous: boolean;
}

export interface MotTest {
  id: string;
  motTestNumber?: string;
  completedDate?: string;
  /** Null for a FAILED test - a failure grants no certificate and so no expiry. */
  expiryDate?: string;
  testResult?: string;
  passed: boolean;
  odometerValue?: string;
  odometerUnit?: string;
  odometerResultType?: string;
  dataSource?: string;
  registrationAtTimeOfTest?: string;
  /** Heavy vehicles only. */
  location?: string;
  defects: MotDefect[];
}

/** What DVSA knows about a plate, shown for confirmation before anything is saved. */
export interface VehicleLookupResult {
  found: boolean;
  registration: string;
  make?: string;
  model?: string;
  colour?: string;
  fuelType?: string;
  engineSizeCc?: string;
  year?: number;
  firstUsedDate?: string;
  registrationDate?: string;
  hasOutstandingRecall?: string;
  motStatus: MotStatus;
  motExpiry?: string;
  motTestDueDate?: string;
  motTestCount: number;
  /** True when the plate is already registered to another account. */
  alreadyRegistered: boolean;
  /** Set when DVSA could not be reached - distinct from "no record". */
  failureReason?: string;
  motSummary?: string;
  /**
   * Vehicle type inferred from the DVSA make and model, for the picker to pre-select.
   * Undefined when the data does not support a guess - leave the current selection alone.
   */
  suggestedVehicleType?: VehicleType;
}

export interface UserVehicle {
  id: string;
  vehicleType: VehicleType;
  registration: string;
  make?: string;
  model?: string;
  year?: number;
  colour?: string;
  nickname?: string;

  // DVSA-sourced identity
  fuelType?: string;
  engineSizeCc?: string;
  firstUsedDate?: string;
  registrationDate?: string;
  hasOutstandingRecall?: string;

  // MOT
  motExpiry?: string;
  motTestDueDate?: string;
  motStatus: MotStatus;
  motSource: MotSource;
  motLastCheckedAt?: string;
  motCertificateUrl?: string;
  motVerifiedAt?: string;
  motExpiringSoon: boolean;
  /** True when the vehicle satisfies the MOT requirement (Valid or NotYetDue). */
  motCompliant: boolean;
  /** True only when DVSA holds no record, so a manual certificate is the way in. */
  requiresManualMot: boolean;
  /** Server-worded status line, so web and mobile cannot phrase it differently. */
  motSummary: string;

  // Insurance (unchanged - still Ops-reviewed)
  hireRewardInsurer?: string;
  hireRewardPolicyRef?: string;
  hireRewardExpiry?: string;
  hireRewardCertUrl?: string;
  insuranceVerifiedAt?: string;
  insuranceStatus: InsuranceStatus;
  insuranceExpiringSoon: boolean;

  isConfirmedByUser: boolean;
  defaultCarrierId?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

/**
 * Only the plate, type and nickname are sent. Make/model/MOT are re-fetched from DVSA
 * server-side, so there is nothing here for a caller to fake.
 */
export interface RegisterVehicleInput {
  vehicleType: VehicleType;
  registration: string;
  nickname?: string;
  hireRewardInsurer?: string;
  hireRewardPolicyRef?: string;
  hireRewardExpiry?: string;
  defaultCarrierId?: string;
}

export interface UpdateVehicleInput {
  vehicleType?: VehicleType;
  nickname?: string;
  /** Manual fallback only - ignored for a DVSA-verified vehicle. */
  motExpiry?: string;
  motCertificateUrl?: string;
  hireRewardInsurer?: string;
  hireRewardPolicyRef?: string;
  hireRewardExpiry?: string;
  hireRewardCertUrl?: string;
  defaultCarrierId?: string;
}

// ─────────────────────────────────────────────
//  CARRIER EMPLOYEE TYPES
// ─────────────────────────────────────────────
export interface CarrierEmployee {
  id: string;
  fullName: string;
  email: string;
  mobile?: string;
  address?: string;
  isOwner: boolean;
  portalLinkActive: boolean;
  portalLinkToken?: string;
  portalLinkUrl?: string;
  insuranceExpiryDate?: string | null;
  licenceExpiryDate?: string | null;
  createdAt: string;
}

export interface RegisterEmployeeInput {
  fullName: string;
  email: string;
  mobile?: string;
  address?: string;
}

// ─────────────────────────────────────────────
//  WORKSHOP EMPLOYEE TYPES
// ─────────────────────────────────────────────
export interface WorkshopEmployee {
  id: string;
  fullName: string;
  email: string;
  mobile?: string;
  address?: string;
  specialisms: string[];
  isOwner: boolean;
  createdAt: string;
}

export interface RegisterWorkshopEmployeeInput {
  fullName: string;
  email: string;
  mobile?: string;
  address?: string;
  specialisms?: string[];
}

// ─────────────────────────────────────────────
//  JOB ASSIGNMENT TYPES
// ─────────────────────────────────────────────
export interface JobAssignment {
  jobId: string;
  employeeId?: string;
  employeeName?: string;
  vehicleId?: string;
  vehicleRegistration?: string;
  vehicleMake?: string;
  vehicleModel?: string;
  magicLinkUrl?: string;
  assignedAt?: string;
}

// ─────────────────────────────────────────────
//  BAZAAR / CLASSIFIED TYPES
// ─────────────────────────────────────────────

export enum BazaarListingType {
  Sell = 0,
  Buy  = 1,
}

export enum ListingStatus {
  Draft           = 0,
  Active          = 1,
  Sold            = 2,
  Fulfilled       = 3,
  Expired         = 4,
  Cancelled       = 5,
  PendingApproval = 6,
  PendingPayment  = 7,
}

export enum ModerationFlag {
  Clean   = 0,
  Abusive = 1,
  Spam    = 2,
  Fraud   = 3,
}

export enum BazaarPricingType {
  Open   = 0,
  Closed = 1,
}

export enum DeliveryOption {
  IncludesDelivery = 0,
  BuyerArranges    = 1,
  PlatformDelivery = 2,
  NotApplicable    = 3,
}

export enum ClassifiedCoverage {
  Town     = 0,
  County   = 1,
  EntireUK = 2,
}

export enum BazaarQuoteStatus {
  Pending   = 0,
  Accepted  = 1,
  Rejected  = 2,
  Withdrawn = 3,
  Expired   = 4,
}

export enum BazaarOrderStatus {
  PendingDeposit = 0,
  DepositPaid    = 1,
  InProgress     = 2,
  AwaitingCod    = 3,
  Completed      = 4,
  Disputed       = 5,
  Cancelled      = 6,
  Refunded       = 7,
}

export interface ListingMedia {
  url: string;
  type: 'image' | 'video';
  thumbnailUrl?: string;
}

export interface BazaarListing {
  id: string;
  listingType: BazaarListingType;
  status: ListingStatus;
  sellerId: string;
  sellerName: string;
  sellerVerified: boolean;
  title: string;
  description: string;
  category: string;
  categoryId?: string;
  attributes?: { key: string; value: string }[];
  condition?: string;
  intentionPrice: number;
  pricingType: BazaarPricingType;
  deliveryOption: DeliveryOption;
  deliveryPrice?: number;
  location: string;
  postcode?: string;
  lat?: number;
  lng?: number;
  distanceMiles?: number;
  media: ListingMedia[];
  quoteCount: number;
  acceptedQuoteId?: string;
  acceptedPrice?: number;
  contactPhone?: string;
  contactEmail?: string;
  linkedDeliveryJobId?: string;
  platformFee?: number;
  hideOfferCount?: boolean;
  aiModerationFlag?: ModerationFlag;
  aiModerationReason?: string;
  createdAt: string;
  expiresAt?: string;
  soldAt?: string;
  isFavourited?: boolean;
  favouriteId?: string;
  favouritedCount?: number;
}

export interface ClassifiedListing {
  id: string;
  status: ListingStatus;
  sellerId: string;
  sellerName: string;
  sellerVerified: boolean;
  title: string;
  description: string;
  category: string;
  condition?: string;
  intentionPrice: number;
  deliveryOption: DeliveryOption;
  deliveryPrice?: number;
  location: string;
  postcode?: string;
  lat?: number;
  lng?: number;
  distanceMiles?: number;
  coverageArea?: ClassifiedCoverage;
  coverageDetail?: string;
  media: ListingMedia[];
  contactPhone?: string;
  contactEmail?: string;
  aiModerationFlag?: ModerationFlag;
  aiModerationReason?: string;
  createdAt: string;
  expiresAt?: string;
  soldAt?: string;
  isFavourited?: boolean;
  favouriteId?: string;
  feeAmount?: number;
  feePaidAt?: string;
}

export interface BazaarFavouriteSummary {
  favouriteId: string;
  listingId: string;
  listingType: BazaarListingType;
  status: ListingStatus;
  title: string;
  description: string;
  category: string;
  intentionPrice: number;
  location: string;
  media: ListingMedia[];
  sellerId: string;
  isFavourited: boolean;
  createdAt: string;
}

export interface ClassifiedFavouriteSummary {
  favouriteId: string;
  listingId: string;
  status: ListingStatus;
  title: string;
  description: string;
  category: string;
  intentionPrice: number;
  location: string;
  media: ListingMedia[];
  sellerId: string;
  isFavourited: boolean;
  createdAt: string;
}

export type SearchRadius = 5 | 10 | 25 | 50 | 100 | 200 | 'nationwide';

export interface BazaarSearchParams {
  listingType?: BazaarListingType;
  category?: string;
  search?: string;
  minPrice?: number;
  maxPrice?: number;
  condition?: string;
  deliveryOption?: DeliveryOption;
  postcode?: string;
  lat?: number;
  lng?: number;
  radiusMiles?: number;
  sortBy?: 'price_asc' | 'price_desc' | 'newest' | 'nearest' | 'attr_asc' | 'attr_desc';
  categoryId?: string;
  page?: number;
  pageSize?: number;
  excludeId?: string;
  /** JSON-encoded ListingAttributeFilter[]. Ignored by the API unless categoryId is also set. */
  attributeFilters?: string;
  /** The attribute 'attr_asc'/'attr_desc' sorts on; the API ignores any key not marked isSortable. */
  sortAttrKey?: string;
}

export interface BazaarBrowseResult {
  items: BazaarListing[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface ClassifiedSearchParams {
  category?: string;
  search?: string;
  minPrice?: number;
  maxPrice?: number;
  condition?: string;
  deliveryOption?: DeliveryOption;
  postcode?: string;
  lat?: number;
  lng?: number;
  radiusMiles?: number;
  sortBy?: 'price_asc' | 'price_desc' | 'newest' | 'nearest' | 'attr_asc' | 'attr_desc';
  coverageArea?: ClassifiedCoverage;
  categoryId?: string;
  /** JSON-encoded ListingAttributeFilter[]. Ignored by the API unless categoryId is also set. */
  attributeFilters?: string;
  sortAttrKey?: string;
}

export interface BazaarQuote {
  id: string;
  listingId: string;
  listingTitle: string;
  quoterId: string;
  quoterName: string;
  quoterVerified: boolean;
  offerPrice: number;
  message?: string;
  mediaUrls?: string[];
  status: BazaarQuoteStatus;
  createdAt: string;
  respondedAt?: string;
}

export interface SubmitBazaarQuoteInput {
  offerPrice: number;
  message?: string;
}

export interface BazaarOrderItem {
  id: string;
  listingId: string;
  listingTitle: string;
  sellerId: string;
  sellerName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  depositAmount: number;
  codAmount: number;
  deliveryPrice: number;
  platformFee: number;
  linkedDeliveryJobId?: string;
  /** Set once this item's seller has been paid out (OTP-verified COD confirmation released their online-paid amount). */
  paymentReleasedAt?: string;
  fulfilmentMethod: BazaarFulfilmentMethod;
}

export enum BazaarFulfilmentMethod {
  Unset               = 0,
  /** Seller pays — "Free Shipping" to the buyer. May still buy a label via ShipEngine. */
  FreeShipping        = 1,
  /** ShipEngine-calculated shipping, cost owed by the buyer. */
  BuyerPaysShipEngine = 2,
  /** Free collection — buyer collects in person. */
  Collection          = 3,
  /** TransitDeal delivery job — medium/large items. */
  TransitDealJob      = 4,
}

export interface BazaarOrder {
  id: string;
  buyerId: string;
  buyerName: string;
  items: BazaarOrderItem[];
  totalDepositPaid: number;
  totalCodDue: number;
  totalPlatformFees: number;
  status: BazaarOrderStatus;
  stripePaymentIntentId?: string;
  cashbackEarned?: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export enum BazaarShipmentStatus {
  AwaitingRate   = 0,
  RatesFetched   = 1,
  LabelPurchased = 2,
  Voided         = 3,
  InTransit      = 4,
  Delivered      = 5,
  Exception      = 6,
}

export interface ShippingAddressInput {
  name: string;
  phone: string;
  addressLine1: string;
  addressLine2?: string;
  cityLocality: string;
  stateProvince?: string;
  postalCode: string;
  countryCode?: string;
}

export interface ShippingRateQuote {
  rateId: string;
  carrierId: string;
  carrierCode: string;
  serviceCode: string;
  serviceName: string;
  amount: number;
  currency: string;
  estimatedDeliveryDays: number;
}

export interface BazaarShipment {
  id: string;
  orderItemId: string;
  status: BazaarShipmentStatus;
  carrierCode?: string;
  serviceCode?: string;
  rateAmount?: number;
  trackingNumber?: string;
  labelPdfUrl?: string;
  labelPurchasedAt?: string;
  lastTrackingStatus?: string;
  lastTrackingCheckedAt?: string;
  estimatedDeliveryDate?: string;
}

export interface CreateBazaarListingInput {
  listingType: BazaarListingType;
  title: string;
  description: string;
  category: string;
  categoryId?: string;
  condition?: string;
  intentionPrice: number;
  pricingType: BazaarPricingType;
  deliveryOption: DeliveryOption;
  deliveryPrice?: number;
  location: string;
  postcode?: string;
  mediaUrls: string[];
  contactPhone?: string;
  contactEmail?: string;
  responsibilityConfirmed: boolean;
  hideOfferCount?: boolean;
  attributes?: ListingAttributeValue[];
}

export interface CreateClassifiedListingInput {
  title: string;
  description: string;
  category: string;
  condition?: string;
  intentionPrice: number;
  deliveryOption: DeliveryOption;
  deliveryPrice?: number;
  location: string;
  postcode?: string;
  coverageArea?: ClassifiedCoverage;
  coverageDetail?: string;
  mediaUrls: string[];
  contactPhone?: string;
  contactEmail?: string;
  responsibilityConfirmed: boolean;
  categoryId?: string;
  attributes?: ListingAttributeValue[];
}

export interface CreateClassifiedListingResult {
  listingId: string;
  clientSecret: string | null;
  feeAmount: number;
}

export interface BazaarAiSuggestInput {
  listingType?: BazaarListingType;
  category?: string;
  keywords?: string;
  condition?: string;
  imageUrls?: string[];
  allowedCategories?: string[];
  /**
   * The draft so far. Sent as INPUT, not just output: a description that already names the
   * make and year is where those values are read from when there is no usable photo. The API
   * treats everything here as ground truth and only ever returns the blanks.
   */
  title?: string;
  description?: string;
  categoryId?: string;
  attributes?: ListingAttributeValue[];
}

/**
 * Suggestions for the BLANKS only. A null/undefined field means "the seller already wrote
 * this" — callers must merge into empty fields rather than assigning, or an untouched field
 * gets wiped.
 */
export interface AiSuggestResult {
  title?: string | null;
  description?: string | null;
  category?: string | null;
  attributes?: ListingAttributeValue[];
}

export interface CreateBazaarOrderItemInput {
  listingId: string;
  quantity: number;
  quoteId?: string;
  deliveryJobId?: string;
  /** Buyer's chosen online-payment amount for this item — must be between item total x the platform's min % and item total. Takes priority over the order-wide payInFull/depositPercent below. */
  onlineAmount?: number;
  /** Buyer can't collect this in person — charged in full online, no cash on delivery. */
  requiresDelivery?: boolean;
}

export interface CreateBazaarOrderInput {
  items: CreateBazaarOrderItemInput[];
  notes?: string;
  /** Buyer chose "pay in full online" — takes priority over depositPercent. */
  payInFull?: boolean;
  /** Buyer's chosen online-payment fraction (0–1) for "cash on delivery, X% online now". Omit for the platform minimum (25%). */
  depositPercent?: number;
}

export interface CreateBazaarOrderResult {
  orderId: string;
  clientSecret: string | null;
  depositAmount: number;
}

export interface ModerationQueueItem {
  id: string;
  listingId: string;
  title: string;
  sellerId: string;
  sellerName: string;
  listingType?: BazaarListingType;
  flag: ModerationFlag;
  aiReason: string;
  createdAt: string;
}

// ─────────────────────────────────────────────
//  CREDIT REQUEST TYPES (admin view)
// ─────────────────────────────────────────────
export interface CreditRequestDto {
  userId: string;
  fullName: string;
  email: string;
  isBusinessAccount: boolean;
  businessName?: string;
  creditApprovalStatus: 'None' | 'Pending' | 'Approved' | 'Rejected';
  creditApprovedAt?: string;
  creditRejectionReason?: string;
  requestedAt?: string;
  identityVerifiedAt?: string;
}

// ─────────────────────────────────────────────
//  INVOICE TYPES
// ─────────────────────────────────────────────
export enum InvoiceStatus {
  Draft = 0,
  Approved = 1,
  Sent = 2,
  Paid = 3,
  Overdue = 4,
  Cancelled = 5,
}

export interface InvoiceLineItemDto {
  description: string;
  quantity: number;
  unitPrice: number;
  vatRate: number;
  amount: number;
  vatAmount: number;
}

export interface InvoiceExtraItemDto {
  description: string;
  amount: number;
  vatRate: number;
  vatAmount: number;
}

export interface InvoiceDto {
  id: string;
  invoiceNumber: string;
  status: InvoiceStatus;
  providerId: string;
  providerName: string;
  providerEmail: string;
  providerPhone?: string;
  providerAddress: string;
  providerBusinessName?: string;
  providerVatNumber?: string;
  providerCompaniesHouseNumber?: string;
  customerId: string;
  customerName: string;
  customerEmail: string;
  customerAddress?: string;
  customerBusinessName?: string;
  customerVatNumber?: string;
  jobIds: string[];
  jobReferences: string[];
  lineItems: InvoiceLineItemDto[];
  extraItems: InvoiceExtraItemDto[];
  subtotal: number;
  vatTotal: number;
  total: number;
  isVatRegistered: boolean;
  notes?: string;
  logoUrl?: string;
  issuedAt: string;
  dueDate?: string;
  paidAt?: string;
  sentAt?: string;
  createdAt: string;
}

export interface CreateInvoiceExtraItemInput {
  description: string;
  amount: number;
  vatRate: number;
}

export interface CreateInvoiceRequest {
  jobIds: string[];
  extraItems?: CreateInvoiceExtraItemInput[];
  notes?: string;
  dueDate?: string;
  isVatRegistered?: boolean;
  vatRate?: number;
}

export interface InvoiceSettingsDto {
  serialPrefix: string;
  nextSerialNumber: number;
  logoUrl?: string;
  defaultNotes?: string;
  defaultVatRate: number;
  showBankDetails: boolean;
}

export interface UpdateInvoiceSettingsRequest {
  serialPrefix?: string;
  nextSerialNumber?: number;
  logoUrl?: string;
  defaultNotes?: string;
  defaultVatRate?: number;
  showBankDetails?: boolean;
}

// ─────────────────────────────────────────────
//  PLATFORM FEE CONFIG TYPES
// ─────────────────────────────────────────────
/**
 * One row of the PlatformFeeConfig rule table — the single home for every platform fee.
 * A rule matches on (feeCode, roleId, channelCode) where null means "any"; the most
 * specific match wins. Rates, which amount they apply to, and whether they apply at all
 * are all data, so a new role or channel needs no code change.
 */
export interface PlatformFeeRule {
  id: number;
  feeCode: string;
  /** null = applies to any role. 0 Customer, 1 Carrier, 2 Workshop. */
  roleId: number | null;
  /** null = applies to any settlement channel. */
  channelCode: string | null;
  /** Which amount `rate` multiplies: GROSS | ONLINE_RECEIVED | COD_PORTION | NONE. */
  basisCode: string;
  /** CHARGE (taken), CREDIT (paid out) or SETTING (a governing value, not money). */
  direction: 'CHARGE' | 'CREDIT' | 'SETTING';
  rate: number;
  flatAmount: number;
  minFee: number | null;
  maxFee: number | null;
  effectiveFrom: string;
  effectiveTo: string | null;
  description: string | null;
  isActive: boolean;
  updatedAt: string;
  updatedByName: string;
}

export type UpsertPlatformFeeRuleInput = Omit<PlatformFeeRule, 'updatedAt' | 'updatedByName'>;

export const FEE_ROLE_LABELS: Record<string, string> = {
  '': 'Any role',
  '0': 'Customer',
  '1': 'Carrier',
  '2': 'Workshop',
};

export const FEE_CHANNEL_LABELS: Record<string, string> = {
  '': 'Any channel',
  STRIPE_CONNECT: 'Stripe',
  COD: 'Cash on delivery',
  INTERNAL: 'TransitDeal wallet',
  BANK_TRANSFER: 'Bank transfer',
};

// ─────────────────────────────────────────────
//  WALLET TYPES
// ─────────────────────────────────────────────
export enum WalletTopUpMethod { Stripe = 0, BankTransfer = 1 }
export enum WalletTopUpStatus { Pending = 0, AwaitingApproval = 1, Completed = 2, Rejected = 3, Failed = 4 }
export enum WalletEntryType { TopUp = 0, Payment = 1, Receipt = 2, Fee = 3, Refund = 4, Withdrawal = 5, Adjustment = 6 }

export interface WalletAccountDto {
  id: string;
  balance: number;
  currency: string;
  isFrozen: boolean;
}

export interface WalletLedgerEntryDto {
  id: string;
  entryType: WalletEntryType;
  /** Signed: positive credits the wallet, negative debits it. */
  amount: number;
  balanceAfter: number;
  referenceType: string | null;
  referenceId: string | null;
  description: string | null;
  createdAt: string;
}

export interface WalletTopUpDto {
  id: string;
  /** What the user asked for, and exactly what the wallet is credited with. */
  amount: number;
  /** Added on top of `amount`. Zero for bank transfers. */
  feeAmount: number;
  /** amount + feeAmount — what the user actually pays. */
  chargedAmount: number;
  method: WalletTopUpMethod;
  status: WalletTopUpStatus;
  bankReference: string | null;
  rejectionReason: string | null;
  createdAt: string;
  completedAt: string | null;
}

export interface PendingWalletTopUpDto extends WalletTopUpDto {
  userId: string;
  userName: string;
  userEmail: string | null;
}

export interface CreateWalletTopUpResponse {
  topUpId: string;
  amount: number;
  feeAmount: number;
  chargedAmount: number;
  method: WalletTopUpMethod;
  status: WalletTopUpStatus;
  bankReference: string | null;
  clientSecret: string | null;
}

/** The registration deposit and what each way of paying it costs. */
export interface DepositQuoteDto {
  amount: number;
  cardFee: number;
  cardTotal: number;
  bankTransferFee: number;
  bankTransferTotal: number;
}

export interface DepositBankTransferInstructionDto {
  amount: number;
  reference: string;
}

// ─────────────────────────────────────────────
//  BAZAAR IDENTITY VERIFICATION TYPES
// ─────────────────────────────────────────────
export enum IdentityVerificationType {
  DrivingLicence = 0,
  UtilityBill = 1,
  Passport = 2,
}

export enum IdentityVerificationStatus {
  NotSubmitted = 0,
  Pending = 1,
  Approved = 2,
  Rejected = 3,
}

export interface IdentityVerificationDto {
  id: string;
  userId: string;
  userName: string;
  verificationType: IdentityVerificationType;
  documentUrl: string;
  status: IdentityVerificationStatus;
  adminNotes?: string;
  submittedAt: string;
  reviewedAt?: string;
  reviewedByName?: string;
}

export interface IdentityVerificationStatusDto {
  status: IdentityVerificationStatus;
  submittedAt?: string;
}

// ─────────────────────────────────────────────
//  BAZAAR REVIEW TYPES (My Reviews - Bazaar tab)
// ─────────────────────────────────────────────
export interface MyBazaarReviewDto {
  reviewId: string;
  orderId: string;
  listingId: string;
  listingTitle: string;
  reviewerId: string;
  reviewerName: string;
  rating: number;
  comment: string | null;
  createdAt: string;
}

export interface BazaarSellerReviewSummary {
  count: number;
  positivePercent: number | null;
  averageRating: number | null;
  itemsSold: number;
  reviews: MyBazaarReviewDto[];
}

export interface BazaarCategory {
  id: string;
  parentId: string | null;
  name: string;
  slug: string;
  displayOrder: number;
}

/** Mirrors AttributeDataType on the server — decides which control the form renders. */
export enum AttributeDataType {
  Text = 0,
  Number = 1,
  Enum = 2,
  Bool = 3,
  Year = 4,
}

/**
 * One attribute a category asks for. Both the create form and the filter sheet render
 * themselves entirely from these — nothing about vehicles or property is hardcoded in
 * either client.
 */
export interface ListingCategoryAttribute {
  id: string;
  /** Stable global key — 'mileage' means vehicle mileage in every category that uses it. */
  attrKey: string;
  label: string;
  dataType: AttributeDataType;
  appliesTo: number;
  unit?: string | null;
  /** Allowed values when dataType is Enum; empty otherwise. */
  options: string[];
  minValue?: number | null;
  maxValue?: number | null;
  isRequired: boolean;
  isFilterable: boolean;
  isSortable: boolean;
  aiHint?: string | null;
  displayOrder: number;
}

/** A spec-sheet value on a listing. `aiFilled` records that the seller accepted an AI suggestion unedited. */
export interface ListingAttributeValue {
  key: string;
  value: string;
  aiFilled?: boolean;
}

/**
 * One category facet in a search. `values` ORs within a key; separate entries AND across
 * keys. Range filters use min/max against the numeric shadow of the value.
 */
export interface ListingAttributeFilter {
  key: string;
  values?: string[];
  min?: number;
  max?: number;
}

// ─────────────────────────────────────────────
//  AUTO-QUOTE
//  A provider's rate card. When a matching job publishes, the platform prices it
//  from this card and submits a quote on their behalf.
// ─────────────────────────────────────────────

export const AutoQuoteMode = {
  /** Quote is created hidden from the customer; the provider releases it. Default for a new card. */
  ConfirmFirst: 0,
  /** Quote goes straight to the customer. Opt-in. */
  Instant: 1,
} as const;
export type AutoQuoteMode = typeof AutoQuoteMode[keyof typeof AutoQuoteMode];

export interface AutoQuoteBand {
  bandOrder: number;
  fromMiles: number;
  /** null on the final, open-ended band */
  toMiles: number | null;
  ratePerMile: number;
}

export interface AutoQuoteUplift {
  vehicleType: VehicleType;
  uplift: number;
}

export interface AutoQuoteArea {
  postcode: string;
  centroidLat: number;
  centroidLng: number;
  radiusMiles: number;
}

/** Shared by the GET response and the PUT payload, minus the server-assigned id. */
export interface AutoQuoteRuleBase {
  isEnabled: boolean;
  mode: AutoQuoteMode;
  autoReleaseAfterMinutes: number | null;
  appliesToSameDay: boolean;
  appliesToScheduled: boolean;
  /** 0 means no limit. */
  maxJobDistanceMiles: number;
  calloutFee: number;
  perExtraStopFee: number;
  perTonneRate: number;
  perLdmRate: number;
  tailLiftSurcharge: number;
  twoManSurcharge: number;
  hiabSurcharge: number;
  sameDaySurcharge: number;
  outOfHoursSurcharge: number;
  weekendSurcharge: number;
  bankHolidaySurcharge: number;
  workingHoursFrom: number;
  workingHoursTo: number;
  fuelLevyPercent: number;
  /** Pence. 500 = round to the nearest £5. 0 disables rounding. */
  roundToNearestPence: number;
  /** 0–1. Road charges are billed per day, not per job; turn this down if you work the zone daily. */
  zoneChargeRecoveryFactor: number;
  hasDartChargeAccount: boolean;
  hasTflAutoPay: boolean;
  /** 0 means no limit. */
  maxOpenAutoQuotes: number;
  /** 0 means no limit. */
  dailyAutoQuoteCap: number;
  bands: AutoQuoteBand[];
  uplifts: AutoQuoteUplift[];
  areas: AutoQuoteArea[];
}

export interface AutoQuoteRuleDto extends AutoQuoteRuleBase {
  ruleId: string;
}

export type SaveAutoQuoteRuleRequest = AutoQuoteRuleBase;

export interface AutoQuotePreviewRequest {
  requiredVehicleType: VehicleType;
  distanceMiles: number;
  scheduledAt: string;
  isSameDay?: boolean;
  extraStopCount?: number;
  weightKg?: number | null;
  loadingMetresLdm?: number | null;
  requiresTailLift?: boolean;
  requiresTwoMan?: boolean;
  requiresHiab?: boolean;
  isBankHoliday?: boolean;
}

export interface PricingLineDto {
  code: string;
  label: string;
  amount: number;
}

export interface AutoQuotePreviewDto {
  amount: number;
  platformCommission: number;
  netAmount: number;
  lines: PricingLineDto[];
  zoneDataDegraded: boolean;
}

export interface WorkshopStandardServiceDto {
  serviceId: string;
  serviceCode: string;
  vehicleType: VehicleType | null;
  labourHours: number;
  labourRate: number;
  partsAmount: number;
  isAutoQuoteEnabled: boolean;
}

export interface SaveWorkshopStandardServiceRequest {
  serviceCode: string;
  vehicleType: VehicleType | null;
  labourHours: number;
  labourRate: number;
  partsAmount: number;
  isAutoQuoteEnabled: boolean;
}

// ─────────────────────────────────────────────
//  ROUTE ZONE BADGES
//  Charging zones a job's route passes through. Shown to every role — it is route
//  information, not a quote breakdown, and it explains why a London run costs more
//  without itemising anybody's price.
// ─────────────────────────────────────────────

/** Server sends this single code instead of the real names when an address is hidden. */
export const COARSE_ROUTE_ZONE = 'TOLL_ROUTE';

export const ROUTE_ZONE_LABELS: Record<string, string> = {
  TOLL_ROUTE:        'Toll route',
  LONDON_CC_WEEKDAY: 'Congestion Charge',
  LONDON_CC_WEEKEND: 'Congestion Charge',
  LONDON_ULEZ:       'ULEZ',
  LONDON_LEZ:        'LEZ',
  CAZ_BIRMINGHAM:    'Birmingham CAZ',
  CAZ_BRISTOL:       'Bristol CAZ',
  CAZ_BATH:          'Bath CAZ',
  CAZ_BRADFORD:      'Bradford CAZ',
  CAZ_SHEFFIELD:     'Sheffield CAZ',
  CAZ_PORTSMOUTH:    'Portsmouth CAZ',
  CAZ_TYNESIDE:      'Tyneside CAZ',
  LEZ_GLASGOW:       'Glasgow LEZ',
  LEZ_EDINBURGH:     'Edinburgh LEZ',
  LEZ_DUNDEE:        'Dundee LEZ',
  LEZ_ABERDEEN:      'Aberdeen LEZ',
  DARTFORD:          'Dartford Crossing',
  MERSEY_GATEWAY:    'Mersey Gateway',
  TYNE_TUNNEL:       'Tyne Tunnel',
  M6_TOLL:           'M6 Toll',
  HUMBER_BRIDGE:     'Humber Bridge',
  SILVERTOWN:        'Silvertown Tunnel',
};

/**
 * Human labels for a job's zone codes. Falls back to the raw code so a zone added to the
 * database before the clients ship still renders something meaningful rather than nothing.
 * De-duplicated because the weekday and weekend Congestion Charge rows share one label.
 */
export function routeZoneLabels(codes?: string[] | null): string[] {
  if (!codes || codes.length === 0) return [];
  return [...new Set(codes.map(c => ROUTE_ZONE_LABELS[c] ?? c))];
}

// ─────────────────────────────────────────────
//  PROVIDER AVAILABILITY
//  Holidays, shutdowns and reduced-capacity days. Overlaid on the work calendar
//  and used to gate auto-quote — a provider on holiday is not offered work, and the
//  shading is what makes "why didn't I get quoted?" answerable.
// ─────────────────────────────────────────────

export const AvailabilityKind = {
  Holiday: 0,
  BankHoliday: 1,
  Maintenance: 2,
  CustomBlock: 3,
} as const;
export type AvailabilityKind = typeof AvailabilityKind[keyof typeof AvailabilityKind];

export const AVAILABILITY_KIND_LABELS: Record<number, string> = {
  [AvailabilityKind.Holiday]:     'Holiday',
  [AvailabilityKind.BankHoliday]: 'Bank holiday',
  [AvailabilityKind.Maintenance]: 'Maintenance',
  [AvailabilityKind.CustomBlock]: 'Unavailable',
};

export interface AvailabilityDto {
  availabilityId: string;
  kind: AvailabilityKind;
  /** ISO date, no time component. */
  startDate: string;
  endDate: string;
  isAllDay: boolean;
  startHour?: number | null;
  endHour?: number | null;
  /** Null or 0 means closed; a positive number reduces capacity rather than blocking. */
  capacityOverride?: number | null;
  note?: string | null;
}

export interface AddAvailabilityRequest {
  kind: AvailabilityKind;
  startDate: string;
  endDate: string;
  isAllDay?: boolean;
  startHour?: number | null;
  endHour?: number | null;
  capacityOverride?: number | null;
  note?: string | null;
}

// ─────────────────────────────────────────────
//  DELIVERY ESTIMATE
//  "Delivery from £X" on Bazaar listings and checkout. Batched — a browse page asks
//  once for the cards in the viewport, not once per card.
// ─────────────────────────────────────────────

export interface DeliveryEstimateItem {
  key: string;
  fromLat: number;
  fromLng: number;
  toLat: number;
  toLng: number;
  vehicleType?: VehicleType;
  /** True on the browse tier, where the seller's location is only known to outcode precision. */
  isIndicative?: boolean;
}

export interface DeliveryEstimateResult {
  key: string;
  /** Null means show no figure — usually too few matching carriers for it to mean anything. */
  fromAmount: number | null;
  providerCount: number;
  isIndicative: boolean;
}

/** "Delivery from ~£45" pre-purchase, "Delivery from £45" once both postcodes are known. */
export function formatDeliveryEstimate(r: DeliveryEstimateResult | null | undefined): string | null {
  if (!r || r.fromAmount == null) return null;
  return `Delivery from ${r.isIndicative ? '~' : ''}£${r.fromAmount.toFixed(2)}`;
}
